import { useEffect, useMemo, useState } from 'react'
import { FieldType, FieldValue, option, FilterField, Filters, O, Option, useLocalStorageActions } from '../../../../../features/ui'
import { useCustomerLoaders, SaleOperation, CustomerField } from '../../../../sales'
import { CreditPayment } from '../../../state/types'
import { toDate } from '../../../../../utils/typeUtils'
import { useLocation } from 'react-router-dom'

type Props = {
    operations: SaleOperation[]
    creditPayments: CreditPayment[]
    onChangeCustomer: (customerId?: string) => void
    onSearchStart: () => void
    onSearchDone: (operationsList: SaleOperation[], creditPaymentsList: CreditPayment[]) => void
    onDateChange: (from?: Date, to?: Date) => void
}

export const CreditAccountFilters = (props: Props) => {
    const { operations, creditPayments, onChangeCustomer, onSearchStart, onSearchDone, onDateChange } = props
    
    const { getFiltersData } = useLocalStorageActions()
    const filtersKey = 'creditAccount'
    const filtersData = useMemo(() => getFiltersData()[filtersKey], [])
    const { state } = useLocation()

    const [dateFrom, setDateFrom] = useState<O<Date>>(toDate(filtersData?.dateFrom) || new Date(new Date().setDate(new Date().getDate() - 60)))
    const [dateTo, setDateTo] = useState<O<Date>>(toDate(filtersData?.dateTo) || new Date())
    const [customerOptions, setCustomerOptions] = useState<Option[]>([])
    const [customerId, setCustomerId] = useState<O<string>>(state?.customerId || filtersData?.customerId)

    const customerLoaders = useCustomerLoaders()

    const loadCustomerOptions = async () => {
        const { loadAllCustomers } = customerLoaders()
        const customers = await loadAllCustomers(true)
        return customers.map(customer =>
            option(customer.id, customer.name)
        )
    }

    useEffect(() => {
        loadCustomerOptions().then(setCustomerOptions)
    }, [])

    useEffect(() =>
        search(dateFrom, dateTo, customerId)
    , [operations])

    useEffect(() => {
        onChangeCustomer(customerId)
    }, [customerId])

    useEffect(() => {
        if (dateFrom || dateTo) {
            onDateChange(dateFrom || undefined, dateTo || undefined)
        }
    }, [dateFrom, dateTo])

    const search = (
        dateFromParam?: Date,
        dateToParam?: Date,
        customerIdParam?: string
    ) => {
        onSearchStart()
        let operationList = operations.filter(operation => !dateFromParam || new Date(operation.creationDate) >= dateFromParam)
        operationList = operationList.filter(operation => !dateToParam || new Date(operation.creationDate) <= dateToParam)
        operationList = operationList.filter(operation => !customerIdParam || operation.customerId === customerIdParam)
        let creditPaymentList = creditPayments.filter(creditPayment => !dateFromParam || new Date(creditPayment.creationDate) >= dateFromParam)
        creditPaymentList = creditPaymentList.filter(creditPayment => !dateToParam || new Date(creditPayment.creationDate) <= dateToParam)
        creditPaymentList = creditPaymentList.filter(creditPayment => !customerIdParam || creditPayment.customerId === customerIdParam)
        setTimeout(() => onSearchDone(operationList, creditPaymentList), 100)
    }

    const handleDateFromChange = (value?: FieldValue) => {
        const newDateFrom = value as O<Date>
        newDateFrom?.setHours(0, 0, 0, 0)
        setDateFrom(newDateFrom)
        search(newDateFrom, dateTo, customerId)
    }

    const handleDateToChange = (value?: FieldValue) => {
        const newDateTo = value as O<Date>
        newDateTo?.setHours(23, 59, 59, 999)
        setDateTo(newDateTo)
        search(dateFrom, newDateTo, customerId)
    }

    const handleCustomerChange = (value?: FieldValue) => {
        const newCustomerId = value as O<string>
        setCustomerId(newCustomerId)
        search(dateFrom, dateTo, newCustomerId)
    }

    const filterFields: FilterField[] = [
        {
            ...CustomerField({
                active: true
            }),
            value: customerId,
            space: 4,
            options: customerOptions,
            onChange: handleCustomerChange
        },
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            label: 'Fecha: Desde',
            value: dateFrom,
            max: dateTo,
            space: 2,
            onChange: handleDateFromChange
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            label: 'Fecha: Hasta',
            value: dateTo,
            min: dateFrom,
            space: 2,
            onChange: handleDateToChange
        }
    ]

    return (
        <Filters
            filtersKey={filtersKey}
            fields={filterFields}
        />
    )
}
